import { Fragment, memo } from "react";

import glyph from "@assets/logo/letterhead-glyph.png";
import tagline from "@assets/logo/letterhead-tagline.png";
import wordmark from "@assets/logo/letterhead-wordmark.png";

import { cn } from "@utils/ui";

import {
  CASE_SUMMARY_ABOUT,
  CASE_SUMMARY_DOCUMENT,
  CASE_SUMMARY_PLACEHOLDER,
} from "./constants";

type CaseSummaryBodyProps = {
  /** Receives the document's container (selection and copy read it). */
  containerRef: (element: HTMLElement | null) => void;
  /** Subject · matter type · date · recipient. */
  subtitle?: string;
};

// Small tracked capitals: the only capitals the brand guidelines allow in a
// document.
const EYEBROW = "text-[9.5px] font-bold uppercase tracking-[0.06em]";

/**
 * The summary as a generated document (MDRS-486), laid out per the MEDirect
 * Documentation Summary Brand Guidelines — a white sheet on the canvas with a
 * letterhead, About block, numbered sections and footer — in the app's theme
 * colours. Memoised so the panel's own state changes (selection, copy
 * feedback) don't re-render it.
 */
export const CaseSummaryBody = memo(
  ({ containerRef, subtitle }: CaseSummaryBodyProps) => {
    const { kind, kindDetail, productLine, title, footer } = CASE_SUMMARY_DOCUMENT;
    const { generated, notOpinion, review } = CASE_SUMMARY_ABOUT;
    const { notice, sections } = CASE_SUMMARY_PLACEHOLDER;

    return (
      <div className="min-h-full bg-background p-[18px]">
        <article
          ref={containerRef}
          data-testid="case-summary-body"
          className={cn(
            "mx-auto max-w-[1000px] rounded-[3px] border border-border bg-background-white shadow-[0_8px_30px_hsl(var(--primary)/0.11)]",
            // 40/48 in a wide panel, easing to 26/20 in a narrow one:
            // percentages follow the panel's width, not the viewport's.
            "px-[clamp(20px,6%,48px)] py-[clamp(26px,5%,40px)]",
            "font-body text-[11.5px] tabular-nums leading-[1.75] text-primary"
          )}
        >
          <header className="mb-[26px] flex flex-wrap items-end justify-between gap-x-5 gap-y-2.5 border-b-2 border-primary pb-3.5">
            {/* Set heights only: the parts keep the artwork's proportions. */}
            <div className="flex items-center gap-[13px]">
              <img src={glyph} alt="" className="block h-12 w-auto" />
              <div className="flex flex-col items-start gap-0.5">
                <img src={wordmark} alt="MEDirect" className="block h-5 w-auto" />
                <img
                  src={tagline}
                  alt="Medicolegal. Evolved."
                  className="block h-[11px] w-auto"
                />
              </div>
            </div>
            <div className="text-right font-head text-[11px] font-bold leading-[1.4]">
              {kind}
              <br />
              {kindDetail}
              <span
                className={cn(
                  EYEBROW,
                  "mt-1.5 block font-semibold leading-none text-foreground-muted"
                )}
              >
                {productLine}
              </span>
            </div>
          </header>

          {/* Sizes and weight override the app's base heading style. */}
          <h3
            className={cn(
              "max-w-[34ch] text-[19px] font-bold leading-[1.25] tracking-[-0.015em]",
              subtitle ? "mb-1" : "mb-[22px]"
            )}
          >
            {title}
          </h3>
          {subtitle && (
            <p className="mb-[22px] text-foreground-muted">
              {/* Wrap between segments, never inside one (e.g. a date). */}
              {subtitle.split(" · ").map((segment, index) => (
                <Fragment key={index}>
                  {index > 0 && " · "}
                  <span className="whitespace-nowrap">{segment}</span>
                </Fragment>
              ))}
            </p>
          )}

          <div className="mb-[26px] border-l-[3px] border-primary bg-background px-[18px] py-3.5 text-[11px] leading-[1.7]">
            <span className={cn(EYEBROW, "mb-[5px] block")}>
              About this document
            </span>
            {generated} <strong>{notOpinion}</strong> <strong>{review}</strong>{" "}
            {notice}
          </div>

          {sections.map((section) => (
            <section key={section.number}>
              <h4 className="mt-[34px] flex items-baseline gap-2.5 border-b border-border pb-[7px] text-[13.5px] font-bold tracking-[-0.005em]">
                <span
                  className={cn(
                    EYEBROW,
                    "inline-block min-w-[34px] text-foreground-muted"
                  )}
                >
                  {section.number}
                </span>{" "}
                {section.title}
              </h4>
              <p className="mt-3">{section.body}</p>
            </section>
          ))}

          <footer
            className={cn(
              EYEBROW,
              "mt-[38px] flex flex-wrap justify-between gap-4 border-t-2 border-primary pt-3 font-semibold text-foreground-muted"
            )}
          >
            {footer.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </footer>
        </article>
      </div>
    );
  }
);
CaseSummaryBody.displayName = "CaseSummaryBody";
